import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import { useAuthStore, useTenantStore } from '@/stores'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Identicon } from '@/components/Identicon'
import { PlanString } from '@/stores/TenantStore'
import type { Plan } from '@core/models'
import { RootPlan, BetaFreePlan } from '@core/models'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

const TenantDetail = observer(() => {
  const { tenantId } = useParams<{ tenantId: string }>()
  const navigate = useNavigate()
  const authStore = useAuthStore()
  const tenantStore = useTenantStore()
  const [editDrawerOpen, setEditDrawerOpen] = useState(false)
  const [editData, setEditData] = useState({
    name: '',
    description: '',
    plan: BetaFreePlan,
  })

  useEffect(() => {
    if (tenantId) {
      tenantStore.getTenant(tenantId)
    }
  }, [tenantId, tenantStore])

  useEffect(() => {
    if (tenantStore.currentTenant) {
      setEditData({
        name: tenantStore.currentTenant.name,
        description: '',
        plan: tenantStore.currentTenant.plan,
      })
    }
  }, [tenantStore.currentTenant])

  const handleSwitchTenant = async () => {
    if (tenantId) {
      authStore.startSpoofing(tenantId)
      // 新しいテナント情報を取得してプランを更新
      await tenantStore.getTenant(tenantId)
      navigate('/')
    }
  }

  const handleEndSpoofing = async () => {
    const originalTenantId = authStore.originalTenantId
    authStore.endSpoofing()
    // 元のテナント情報を再取得
    if (originalTenantId) {
      await tenantStore.getTenant(originalTenantId)
    }
    navigate('/')
  }

  const handleSave = async () => {
    if (tenantId) {
      const success = await tenantStore.updateTenant(tenantId, editData)
      if (success) {
        setEditDrawerOpen(false)
      }
    }
  }

  if (!tenantStore.currentTenant) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">読み込み中...</div>
      </div>
    )
  }

  const tenant = tenantStore.currentTenant

  return (
    <>
      <div className="space-y-6 mb-6">
        <h1 className="text-3xl font-bold">テナント情報</h1>
      </div>
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <Identicon source={tenant.id} size={96} className="rounded" />
              <div className="space-y-2">
                <CardTitle className="text-2xl">{tenant.name}</CardTitle>
                <CardDescription className="space-y-1">
                  <div>ID: {tenant.id}</div>
                  <Badge variant={tenant.plan === RootPlan ? 'destructive' : 'secondary'}>
                    {PlanString[tenant.plan] || 'Unknown'}
                  </Badge>
                </CardDescription>
              </div>
            </div>
            <div className="flex gap-2">
              {authStore.isRootTenant && !authStore.isSpoofing && (
                <Button variant="outline" onClick={handleSwitchTenant}>
                  なりすまし管理
                </Button>
              )}
              {authStore.isSpoofing && authStore.currentTenantId === tenantId && (
                <Button variant="destructive" onClick={handleEndSpoofing}>
                  元のテナントに戻る
                </Button>
              )}
              {authStore.isRootTenant && (
                <Button onClick={() => setEditDrawerOpen(true)}>
                  編集
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium mb-2">作成日時</h3>
              <p className="text-sm text-muted-foreground">
                {tenant.created ? new Date(tenant.created).toLocaleString('ja-JP') : '-'}
              </p>
            </div>
            <div>
              <h3 className="text-sm font-medium mb-2">更新日時</h3>
              <p className="text-sm text-muted-foreground">
                {tenant.updated ? new Date(tenant.updated).toLocaleString('ja-JP') : '-'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Sheet open={editDrawerOpen} onOpenChange={setEditDrawerOpen}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>テナント編集</SheetTitle>
            <SheetDescription>
              テナント情報を編集します
            </SheetDescription>
          </SheetHeader>
          <div className="space-y-4 mt-6">
            <div className="space-y-2">
              <Label htmlFor="name">テナント名</Label>
              <Input
                id="name"
                value={editData.name}
                onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                placeholder="テナント名を入力"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">説明</Label>
              <Textarea
                id="description"
                value={editData.description}
                onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                placeholder="説明を入力"
                rows={4}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="plan">プラン</Label>
              <Select 
                value={String(editData.plan)} 
                onValueChange={(value) => setEditData({ ...editData, plan: Number(value) as Plan })}
              >
                <SelectTrigger id="plan">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={String(BetaFreePlan)}>βフリープラン</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setEditDrawerOpen(false)}
                className="flex-1"
              >
                キャンセル
              </Button>
              <Button
                onClick={handleSave}
                className="flex-1"
                disabled={tenantStore.isLoading}
              >
                {tenantStore.isLoading ? '保存中...' : '保存'}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  )
})

export default TenantDetail